import React, { useState } from 'react';
import {
    Button,
    Link,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    useDisclosure,
    useToast
} from '@chakra-ui/react';
import { type IFetchHookResponse, useTranslate } from 'ts/common/hooks';

interface Props {
    invoice: SpApi.Client.IInvoice;
    onRemoveSavedCard?: () => Promise<IFetchHookResponse<void>>;
}

const ClientInvoiceSavedCard: React.FC<Props> = ({ invoice, onRemoveSavedCard }) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const t = useTranslate('clientInvoice.savedCard');
    const [isLoading, setIsLoading] = useState(false);
    const toast = useToast();

    const removeSavedCard = async () => {
        setIsLoading(true);

        try {
            await onRemoveSavedCard?.();
        } catch (error) {
            toast({
                title: t('removeSavedCardError'),
                status: 'error'
            });
            return;
        } finally {
            setIsLoading(false);
        }

        toast({
            title: t('removeSavedCardSuccess'),
            status: 'success'
        });

        onClose();
    };

    if (!invoice.creditCardOnFile) {
        return null;
    }

    return (
        <>
            <Text textAlign="center" py={3} px={5}>
                {t('autoPayConfigured', {
                    lastFourDigits: invoice.creditCardOnFile.cardNumber
                })}
                {' - '}
                <Link
                    color="#B01304"
                    fontWeight="600"
                    textDecoration="underline"
                    onClick={onRemoveSavedCard ? onOpen : undefined}
                >
                    {t('removeSavedCard')}
                </Link>
            </Text>
            <Modal isOpen={isOpen} onClose={onClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{t('removeSavedCardModal.heading')}</ModalHeader>
                    <ModalCloseButton border="none" top={4} />
                    <ModalBody px={6} py={4}>
                        <Text>{t('removeSavedCardModal.description')}</Text>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <Button onClick={onClose}>{t('removeSavedCardModal.cancel')}</Button>
                        <Button isLoading={isLoading} onClick={removeSavedCard} variant="primary">
                            {t('removeSavedCardModal.primaryAction')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default ClientInvoiceSavedCard;
